// src/components/dashboard/ManageMemberModal.js
// Updated: 10/20/2025 03:32 PM

import React, { useState, useEffect } from 'react';
import { db } from '../../firebase/config';
import { doc, setDoc, updateDoc, arrayUnion } from 'firebase/firestore';
import { v4 as uuidv4 } from 'uuid';
import toast from 'react-hot-toast';
import Spinner from '../common/Spinner';
import ModalWrapper from '../common/ModalWrapper';

// Normalize Firestore id or DocumentReference -> string id
const getId = (val) => {
  if (!val) return '';
  if (typeof val === 'string') return val;
  if (typeof val === 'object' && val.id) return val.id; // DocumentReference
  return '';
};

// Normalize gender text to consistent labels
const normalizeGender = (g) => {
  const s = (g || '').toString().trim().toLowerCase();
  if (['male', 'm', 'man', 'boy', 'father', 'dad'].includes(s)) return 'Male';
  if (['female', 'f', 'woman', 'girl', 'mother', 'mom', 'mum'].includes(s)) return 'Female';
  return '';
};

const ManageMemberModal = ({
  familyId,
  existingMember,
  allMembers = [],
  onClose,
  onMemberAdded = () => {}, // default no-op prevents runtime error
}) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [gender, setGender] = useState('');
  const [dob, setDob] = useState('');
  const [dod, setDod] = useState('');
  const [isDeceased, setIsDeceased] = useState(false);
  const [loading, setLoading] = useState(false);

  const [motherId, setMotherId] = useState('');
  const [fatherId, setFatherId] = useState('');
  const [spouseId, setSpouseId] = useState('');

  const isEditMode = !!existingMember;

  // Filter out self
  const selectableMembers = (allMembers || []).filter(
    (m) => m && m.id !== existingMember?.id
  );

  // Gender-filtered lists
  const maleMembers = selectableMembers.filter(
    (m) => normalizeGender(m.gender) === 'Male'
  );
  const femaleMembers = selectableMembers.filter(
    (m) => normalizeGender(m.gender) === 'Female'
  );

  useEffect(() => {
    if (existingMember) {
      setFirstName(existingMember.firstName || '');
      setLastName(existingMember.lastName || '');
      setGender(existingMember.gender || '');
      setDob(existingMember.dob || '');
      setDod(existingMember.dod || '');
      setIsDeceased(!!existingMember.isDeceased);
      setMotherId(getId(existingMember.motherId));
      setFatherId(getId(existingMember.fatherId));
      setSpouseId(getId(existingMember.spouseId));
    } else {
      setFirstName('');
      setLastName('');
      setGender('');
      setDob('');
      setDod('');
      setIsDeceased(false);
      setMotherId('');
      setFatherId('');
      setSpouseId('');
    }
  }, [existingMember]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!familyId) {
      toast.error('Missing family context.');
      return;
    }
    setLoading(true);

    const memberData = {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      familyId,
      gender,
      dob: dob?.trim() || '',
      dod: isDeceased ? (dod?.trim() || '') : '',
      isDeceased: !!isDeceased,
      email: isEditMode ? existingMember.email ?? null : null, //
      profilePictureUrl: isEditMode ? existingMember.profilePictureUrl ?? '' : '', //
      motherId: getId(motherId) || null, //
      fatherId: getId(fatherId) || null, //
      spouseId: getId(spouseId) || null, //
    };

    try {
      if (isEditMode) {
        const userDocRef = doc(db, 'users', existingMember.id);
        await updateDoc(userDocRef, memberData); //
        toast.success('Member updated successfully!'); //
      } else {
        const newMemberId = uuidv4(); //
        const userDocRef = doc(db, 'users', newMemberId);
        await setDoc(userDocRef, memberData); //
        const familyDocRef = doc(db, 'families', familyId);
        await updateDoc(familyDocRef, { members: arrayUnion(newMemberId) }); //
        toast.success('Family member added successfully!'); //
      }
      onMemberAdded(); //
      onClose(); //
    } catch (error) {
      console.error('Error saving member: ', error); //
      toast.error('Failed to save member.'); //
    } finally {
      setLoading(false); //
    }
  };

  return (
    <ModalWrapper onClose={onClose} size="lg"> {/* */}
      <div className="p-6 border-b"> {/* */}
        <h3 className="text-xl font-semibold text-neutral-gray"> {/* */}
          {isEditMode ? 'Edit Family Member' : 'Add Family Member'} {/* */}
        </h3>
      </div>

      <form onSubmit={handleSubmit}> {/* */}
        <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto"> {/* */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4"> {/* */}
            <div>
              <label htmlFor="firstName" className="block text-sm font-medium text-gray-700"> {/* */}
                First Name
              </label>
              <input
                type="text"
                id="firstName"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)} //
                required
                className="mt-1 w-full border-gray-300 rounded-md shadow-sm" //
              />
            </div>

            <div>
              <label htmlFor="lastName" className="block text-sm font-medium text-gray-700"> {/* */}
                Last Name
              </label>
              <input
                type="text"
                id="lastName"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)} //
                required
                className="mt-1 w-full border-gray-300 rounded-md shadow-sm" //
              />
            </div>
          </div>

          <div>
            <label htmlFor="gender" className="block text-sm font-medium text-gray-700"> {/* */}
              Gender
            </label>
            <select
              id="gender"
              value={gender}
              onChange={(e) => setGender(e.target.value)} //
              className="mt-1 w-full border-gray-300 rounded-md shadow-sm" //
            >
              <option value="">Select...</option> {/* */}
              <option value="Male">Male</option> {/* */}
              <option value="Female">Female</option> {/* */}
            </select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4"> {/* */}
            <div>
              <label htmlFor="dob" className="block text-sm font-medium text-gray-700"> {/* */}
                Date of Birth
              </label>
              <input
                type="text"
                id="dob"
                placeholder="e.g., 1955 or 03/15/1955" //
                value={dob}
                onChange={(e) => setDob(e.target.value)} //
                className="mt-1 w-full border-gray-300 rounded-md shadow-sm" //
              />
            </div>

            {isDeceased && ( //
              <div>
                <label htmlFor="dod" className="block text-sm font-medium text-gray-700"> {/* */}
                  Date of Death
                </label>
                <input
                  type="text"
                  id="dod"
                  placeholder="e.g., 2010 or 11/22/2010" //
                  value={dod}
                  onChange={(e) => setDod(e.target.value)} //
                  className="mt-1 w-full border-gray-300 rounded-md shadow-sm" //
                />
              </div>
            )}
          </div>

          <div className="flex items-start"> {/* */}
            <div className="flex items-center h-5"> {/* */}
              <input
                id="isDeceased"
                name="isDeceased"
                type="checkbox"
                checked={isDeceased}
                onChange={(e) => setIsDeceased(e.target.checked)} //
                className="focus:ring-primary h-4 w-4 text-primary border-gray-300 rounded" //
              />
            </div>
            <div className="ml-3 text-sm"> {/* */}
              <label htmlFor="isDeceased" className="font-medium text-gray-700"> {/* */}
                This member is deceased
              </label>
            </div>
          </div>

          {/* Family Relationships Section (NOW always shown in edit mode) */}
          {isEditMode && ( //
            <div className="pt-6 border-t"> {/* */}
              <h2 className="text-lg font-medium text-neutral-gray">Family Relationships</h2> {/* */}
              <p className="text-sm text-gray-500 mt-1">Select known relatives. Use the Family Tree page for more complex linking.</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-4"> {/* */}
                {/* Father (male only) */}
                <div>
                  <label htmlFor="fatherId" className="block text-sm font-medium text-gray-700"> {/* */}
                    Father
                  </label>
                  <select
                    id="fatherId"
                    value={fatherId}
                    onChange={(e) => setFatherId(e.target.value)} //
                    className="mt-1 w-full border-gray-300 rounded-md shadow-sm" //
                  >
                    <option value="">Select...</option> {/* */}
                    {maleMembers.map((member) => ( //
                      <option key={member.id} value={member.id}> {/* */}
                        {member.firstName} {member.lastName}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Mother (female only) */}
                <div>
                  <label htmlFor="motherId" className="block text-sm font-medium text-gray-700"> {/* */}
                    Mother
                  </label>
                  <select
                    id="motherId"
                    value={motherId}
                    onChange={(e) => setMotherId(e.target.value)} //
                    className="mt-1 w-full border-gray-300 rounded-md shadow-sm" //
                  >
                    <option value="">Select...</option> {/* */}
                    {femaleMembers.map((member) => ( //
                      <option key={member.id} value={member.id}> {/* */}
                        {member.firstName} {member.lastName}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Spouse (any gender) */}
                <div>
                  <label htmlFor="spouseId" className="block text-sm font-medium text-gray-700"> {/* */}
                    Spouse
                  </label>
                  <select
                    id="spouseId"
                    value={spouseId}
                    onChange={(e) => setSpouseId(e.target.value)} //
                    className="mt-1 w-full border-gray-300 rounded-md shadow-sm" //
                  >
                    <option value="">Select...</option> {/* */}
                    {selectableMembers.map((member) => ( //
                      <option key={member.id} value={member.id}> {/* */}
                        {member.firstName} {member.lastName}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="p-6 bg-gray-50 rounded-b-lg flex justify-end items-center gap-4"> {/* */}
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium hover:bg-gray-50" //
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 bg-primary text-white rounded-md hover:bg-blue-700 text-sm font-medium disabled:opacity-50 flex items-center" //
          >
            {loading ? <Spinner size="sm" /> : isEditMode ? 'Save Changes' : 'Save Member'} {/* */}
          </button>
        </div>
      </form>
    </ModalWrapper>
  );
};

export default ManageMemberModal;